import { Injectable, inject } from '@angular/core';

import { formatLoadDiagnosticCode, formatToastDiagnosticCode, LOAD_FAILURE_COPY } from '../../utils/sheet-load-trace.util';
import { AuthFailureNoticeService } from './auth-failure-notice.service';

/**
 * localStorage key holding the "a customer session existed in this browser" hint.
 *
 * Deliberately OUTSIDE the `mbrs-` / `__mbrs-` prefixes that `clearCustomerDataCache`
 * wipes (same reasoning as `SESSION_TIMING_STORAGE_KEY`): the hint has to survive
 * the browser dropping the expired cookie, and it is cleared explicitly by
 * {@link SessionHintService.clear} when a session ends and the user has been told.
 */
export const SESSION_HINT_STORAGE_KEY = 'yyc-had-session';

/**
 * Remembers that this browser once held a customer session, so a revisit AFTER the
 * session expired can still show the single `MBRS-AUTH` toast.
 *
 * ## Why this exists
 *
 * The auth cookie is `HttpOnly` (this code cannot read it) and persistent: once it
 * expires the browser deletes it, so the backend sees a request with no cookie at
 * all — indistinguishable from a first-time visitor. `isAuthenticatedGuard` used to
 * treat every cold-load "not signed in" as exactly that and redirect silently.
 * A tiny hint written while a session is live is the only signal left that survives
 * the cookie.
 *
 * ## The lifecycle (shown once per login → expiry cycle)
 *
 * 1. {@link markSessionExists} — on a successful customer login, and whenever the
 *    guard confirms a live customer session (covers sessions that began before this
 *    shipped). Idempotent.
 * 2. {@link stageNoticeIfLapsed} — a cold load where the backend says "not signed in"
 *    and the hint is present: stages `MBRS-AUTH` for the login page and clears the hint,
 *    so the next refresh or revisit stays silent.
 * 3. {@link clear} — every teardown (`AuthService.clearAuthState()`): a manual logout,
 *    a forced logout that already staged its own toast, a cross-tab logout. None of
 *    those should produce a second toast later.
 *
 * ## What it is NOT
 *
 * Not auth state. It holds a constant, no identity and no token, and nothing reads it
 * to decide access — only whether to show a notice. A stale hint can therefore never
 * resurrect a session, which is the reason the "never persist auth state" rule exists.
 * It is per browser profile: another browser, a private window or cleared site data
 * has no hint and shows no toast, by design.
 *
 * Every storage access is guarded — the hint must never break auth.
 */
@Injectable({ providedIn: 'root' })
export class SessionHintService {
  private readonly authFailureNotice = inject(AuthFailureNoticeService);

  /** Record that a customer session exists in this browser. Idempotent. */
  markSessionExists(): void {
    try {
      if (window.localStorage.getItem(SESSION_HINT_STORAGE_KEY) === null) {
        window.localStorage.setItem(SESSION_HINT_STORAGE_KEY, '1');
      }
    }
    catch {
      // Storage can be refused (private mode); the toast is a courtesy, never required.
    }
  }

  /** Forget the hint. Safe to call when none is set. */
  clear(): void {
    try {
      window.localStorage.removeItem(SESSION_HINT_STORAGE_KEY);
    }
    catch {
      // See markSessionExists.
    }
  }

  /** Whether this browser holds a hint from a customer session that was never ended through the app. */
  hasHint(): boolean {
    try {
      return window.localStorage.getItem(SESSION_HINT_STORAGE_KEY) !== null;
    }
    catch {
      return false;
    }
  }

  /**
   * For a cold load the backend has just said is NOT signed in: if the hint says a
   * session used to exist here, stage the generic `MBRS-AUTH` toast for the login
   * page and clear the hint (shown once). Returns whether it staged anything.
   *
   * `AuthFailureNoticeService.stage()` is itself a no-op while the diagnostic-codes
   * flag is off (production), so nothing shows there, but the hint is still cleared.
   */
  stageNoticeIfLapsed(): boolean {
    if (!this.hasHint()) {
      return false;
    }
    this.clear();
    // Same composition as `AuthService.forceLogout`'s fallback; `SESSION` is the
    // identifier-free scope, so the toast reads plain `MBRS-AUTH`.
    console.error(`[auth] ${formatLoadDiagnosticCode('SESSION', 'AUTH', null)}`, { reason: 'session-lapsed-before-revisit' });
    this.authFailureNotice.stage(`${LOAD_FAILURE_COPY.AUTH} ${formatToastDiagnosticCode('SESSION', 'AUTH', null)}`);
    return true;
  }
}
